import { STRINGS } from '@/constants/strings';
import type { EditablePlayerGender, PlayerGenderFormValue } from '@/utils/player-gender';
import {
  avatarRule,
  birthDateRule,
  cityRule,
  nameRule,
  playerHeightRule,
  zipCodeRule,
} from '@/validation';
import * as yup from 'yup';

const strings = STRINGS.onboarding.addPlayer;
/** Reuse the coach mutate-player validation copy so mobile messages stay consistent. */
export const MESSAGES = STRINGS.mutatePlayer.validation;

export type PlayerGender = EditablePlayerGender;
export type NeedClubs = 'yes' | 'no';
export type DominantHand = 'left' | 'right';

/** New players always start at level 1; the field is display-only (the API ignores it). */
export const DEFAULT_PLAYER_LEVEL = 1;

export const GENDER_OPTIONS: { value: PlayerGender; label: string }[] = [
  { value: 'male', label: strings.genderMale },
  { value: 'female', label: strings.genderFemale },
];

export const NEED_CLUB_OPTIONS: { value: NeedClubs; label: string }[] = [
  { value: 'no', label: strings.needClubNo },
  { value: 'yes', label: strings.needClubYes },
];

export const DOMINANT_HAND_OPTIONS: { value: DominantHand; label: string }[] = [
  { value: 'right', label: strings.handRight },
  { value: 'left', label: strings.handLeft },
];

/** Min/max selectable player height, in inches. */
export const HEIGHT_MIN_INCHES = 36;
export const HEIGHT_MAX_INCHES = 69;

/**
 * Height options 36"–69". The value matches the `NN"` string the form already stores
 * (and that `playerHeightRule` / the submit `Number(...)` conversion expect), so the
 * saved data stays numeric exactly as before.
 */
export const HEIGHT_OPTIONS: { value: string; label: string }[] = Array.from(
  { length: HEIGHT_MAX_INCHES - HEIGHT_MIN_INCHES + 1 },
  (_, i) => {
    const label = `${HEIGHT_MIN_INCHES + i}"`;
    return { value: label, label };
  },
);

/** Guardian "Add another player" form (a new golfer linked to the guardian). */
export const addPlayerSchema = yup
  .object({
    firstName: nameRule(MESSAGES.fieldRequired),
    lastName: nameRule(MESSAGES.fieldRequired),
    dob: birthDateRule(MESSAGES.fieldRequired, MESSAGES.birthDateCannotBeFuture),
    gender: yup
      .mixed<PlayerGenderFormValue>()
      .oneOf(['male', 'female'], MESSAGES.fieldRequired)
      .required(MESSAGES.fieldRequired),
    height: playerHeightRule(MESSAGES.playerHeightMustBeNumber),
    needClubs: yup.mixed<NeedClubs>().oneOf(['yes', 'no']).required(MESSAGES.fieldRequired),
    dominantHand: yup
      .mixed<DominantHand>()
      .oneOf(['left', 'right'])
      .required(MESSAGES.fieldRequired),
    sameAddress: yup.boolean().defined(),
    // Holds the ISO code (e.g. "US"); mapped to the country name when sent to the API.
    country: yup.string().trim().defined(),
    city: cityRule(),
    state: yup.string().trim().defined().nullable(),
    zipCode: zipCodeRule(),
    avatar: avatarRule(MESSAGES.fieldRequired, MESSAGES.avatarTooLarge),
  })
  .required();

export type AddPlayerForm = {
  firstName: string;
  lastName: string;
  dob: Date | null;
  gender: PlayerGenderFormValue;
  height: string;
  needClubs: NeedClubs;
  dominantHand: DominantHand;
  sameAddress: boolean;
  country: string;
  city: string;
  state: string | null;
  zipCode: string;
  avatar?: { uri: string; fileSize?: number } | null;
};

export const INITIAL_ADD_PLAYER_FORM: AddPlayerForm = {
  firstName: '',
  lastName: '',
  dob: null,
  gender: '',
  height: '',
  needClubs: 'no',
  dominantHand: 'right',
  sameAddress: false,
  country: '',
  city: '',
  state: null,
  zipCode: '',
  avatar: null,
};
